import React, { useState, useEffect, useRef, useCallback } from "react";
import Grid from "@mui/material/Grid2";

import { 
  Campaign,
  QuestBoard,
} from "@dndModels";

import AddButton from "@components/input/buttons/icon_buttons/AddButton";
import DandDAPI from "@utilities/smart_api";
import LoadingBox from "@components/input/LoadingBox";
import CommonUtilities from "@utilities/common_utilities";
import FlexBox from "@components/layout/FlexBox";
import QuestBoardInput from "./QuestBoardInput";
import RefreshButton from "@components/input/buttons/icon_buttons/RefreshButton";
import ViewArchivesCheckBox from "@dndComponents/campaign/ViewArchivesCheckBox";
import { useSearchParams } from "react-router-dom";

interface Props {
  campaign: Campaign;
}

const api = DandDAPI.getInstance();
const common_util = CommonUtilities.getInstance();

export default function CampaignQuestBoardsInput({
  campaign,
}: Props) {
  const [searchParams, setSearchParams] = useSearchParams();
  const quest_board_param = searchParams.get("quest_board");
  const [fetched, setFetched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [reloading, setReloading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [view_archives, setViewArchives] = useState(false);
  const mounted = useRef(false);

  useEffect(() => { 
    mounted.current = true;

    return () => {
      mounted.current = false;
    };
  }, []);

  const load = useCallback( async () => {
    // debugger;
    if (campaign.quest_boards.length === 0) {
      campaign.quest_boards = await api.getCampaignObjects<QuestBoard>({ data_type: "campaign_quest_board", campaign });
    }
    setLoading(false);
  }, [campaign]);

  useEffect(() => {
    if (!fetched) {
      setFetched(true);
      setLoading(true);
      setTimeout(() => {
        load();
      }, 100);
    }
  }, [fetched, load]);
    
  useEffect(() => {
    if (reloading) {
      setReloading(false);
    }
  }, [reloading, setReloading]);

  const handleQuestBoardOpenChange = useCallback((quest_board_id: string, open: boolean) => {
    setSearchParams(prev => {
      const params = new URLSearchParams(prev);
      if (open) {
        params.set("quest_board", quest_board_id);
      } else if (params.get("quest_board") === quest_board_id) {
        // Only clear if this quest_board owns the param — avoids a stale
        // close callback from a previously-open modal wiping a newer one.
        params.delete("quest_board");
      }
      return params;
    }, { replace: true });
  }, [setSearchParams]);

  const renderQuestBoards = () => {
    return (
      <FlexBox>
        <Grid container spacing={0} direction="row">
          { (view_archives ? campaign.archived_quest_boards : campaign.quest_boards).map(quest_board => {
            return (
              <Grid size={{ xs:12, sm: 6, md: 4, lg: 3 }} key={quest_board._id}>
                <QuestBoardInput
                  campaign={campaign}
                  quest_board={quest_board} 
                  autoOpen={quest_board._id === quest_board_param}
                  onOpenChange={(open: boolean) => handleQuestBoardOpenChange(quest_board._id, open)}
                  onDelete={ async () => {
                    setProcessing(true);
                    await api.deleteObject("campaign_quest_board", quest_board);
                    campaign.quest_boards = campaign.quest_boards.filter(o => o._id !== quest_board._id);
                    campaign.archived_quest_boards = campaign.archived_quest_boards.filter(o => o._id !== quest_board._id);
                    setProcessing(false);
                  }} 
                  onArchive={ async () => {
                    setProcessing(true);
                    await api.setCampaignObjectArchived({ campaign, data_type: "campaign_quest_board", obj: quest_board, is_archived: !quest_board.is_archived });
                    setProcessing(false);
                  }}
                />
              </Grid>
            );
          })}
        </Grid>
      </FlexBox>
    )
  }

  if (!fetched || loading) {
    return (<LoadingBox />);
  }
  return (
    <FlexBox direction="column" style={{ width: "100%" }}>
      { api.user && api.user._id === campaign.owner_id &&
        <FlexBox direction="row">
          <FlexBox xs_expand={0} className="padded">
            <AddButton
              disabled={processing}
              onClick={ async () => {
                const new_quest_board = new QuestBoard();
                new_quest_board.campaign_id = campaign._id;
                setProcessing(true);
                await api.upsertObject("campaign_quest_board", new_quest_board);
                campaign.quest_boards.push(new_quest_board);
                setProcessing(false);
              }}
            />
          </FlexBox>
          <FlexBox xs_expand={0} className="padded">
            <RefreshButton
              disabled={processing}
              onClick={ async () => {
                setProcessing(true);
                if (view_archives) {
                  await api.loadArchivedCampaignObjects({ campaign, data_type: "campaign_quest_board", refresh: true });
                } else {
                  campaign.quest_boards = [];
                  campaign.quest_boards = await api.getCampaignObjects<QuestBoard>({ data_type: "campaign_quest_board", campaign });
                }
                setProcessing(false);
              }}
            />
          </FlexBox>
          <FlexBox xs_expand={0} className="padded">
            <ViewArchivesCheckBox
              campaign={campaign}
              data_type="campaign_quest_board"
              value={view_archives}
              disabled={processing}
              onChange={setViewArchives}
            />
          </FlexBox>
        </FlexBox>
      }
      { renderQuestBoards() }
    </FlexBox>
  );
}
